import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", date: "", location: "", description: "", category: "" };

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/events").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/events/${editing.id}`, form);
      else await api.post("/events", form);
      toast.success(editing ? "Événement mis à jour" : "Événement ajouté");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Supprimé"); load();
  };

  return (
    <div data-testid="admin-events">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Agenda</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Événements</h1>
        </div>
        <Button onClick={openCreate} className="bg-gold hover:bg-gold-hover text-white rounded-full" data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Lieu</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucun événement</TableCell></TableRow>
            )}
            {items.map((e) => (
              <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
                <TableCell className="font-medium text-deepblue-900">{e.title}</TableCell>
                <TableCell>{new Date(e.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                <TableCell>{e.location}</TableCell>
                <TableCell>{e.category || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier" : "Ajouter"} un événement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titre *</Label><Input value={form.title} onChange={upd("title")} data-testid="event-form-title" /></div>
            <div><Label>Date & heure *</Label><Input type="datetime-local" value={(form.date || "").slice(0, 16)} onChange={upd("date")} data-testid="event-form-date" /></div>
            <div><Label>Lieu *</Label><Input value={form.location} onChange={upd("location")} data-testid="event-form-location" /></div>
            <div><Label>Catégorie</Label><Input value={form.category} onChange={upd("category")} placeholder="Retraite, Musique, Baptême…" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={upd("description")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-gold hover:bg-gold-hover text-white" data-testid="save-event-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
